import { useSyncExternalStore } from 'react';

// Single source of truth for the selected subaccount id, deliberately decoupled
// from AuthContext so the data layer (get/getList) can fold it into the
// react-query cache key WITHOUT importing auth.tsx (which itself imports
// useApi — that would be a cycle). Keying queries by subaccount is what stops
// one account's cached financial data from rendering under another during an
// account switch; the id is also still injected into the request params by the
// httpClient interceptor (it reads the same localStorage key this store writes).
const STORAGE_KEY = 'selectedSubaccountId';

let currentId: string | null = typeof localStorage === 'undefined'
  ? null
  : localStorage.getItem(STORAGE_KEY);

const listeners = new Set<() => void>();

export const setSelectedSubaccountId = (id: string | null): void => {
  currentId = id;

  if (typeof localStorage !== 'undefined') {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  listeners.forEach((listener) => listener());
};

export const getSelectedSubaccountId = (): string | null => currentId;

const subscribe = (callback: () => void): (() => void) => {
  listeners.add(callback);

  return () => {
    listeners.delete(callback);
  };
};

// Reactive read for the data layer: a switch re-renders every subscribed useApi
// hook with the new id, so each account resolves to its own cache entry.
export const useSelectedSubaccountId = (): string | null =>
  useSyncExternalStore(subscribe, getSelectedSubaccountId);
